/**
 * THE DAY «سودوکوی روز» BELONGS TO — the calendar day in Asia/Tehran, which turns at local midnight.
 *
 * Iran keeps one offset all year (UTC+3:30; daylight saving ended in 2022), so a Tehran day starts at
 * 20:30 UTC of the day before, every day: 20:29:59 UTC is still the old day, 20:30:00 the new one.
 * A fixed offset, not `Intl` — exact, and the same on every server and phone.
 */
import type { SudokuLevel, SudokuSize } from "./grid";

/**
 * «سودوکوی روز» is a 9×9 «متوسط» (the coordinator's ruling); its time counts toward that record. Here,
 * not in `bank.ts`, so the phone (which never imports the bank) shows the very size and level the
 * server serves; `bank.ts` re-exports both.
 */
export const DAILY_SIZE: SudokuSize = 9;
export const DAILY_LEVEL: SudokuLevel = "medium";

/** UTC+3:30 in milliseconds. */
export const TEHRAN_OFFSET_MS = (3 * 60 + 30) * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The Tehran calendar day of an instant, as `"YYYY-MM-DD"`. */
export function tehranDayKey(nowMs: number): string {
  return new Date(nowMs + TEHRAN_OFFSET_MS).toISOString().slice(0, 10);
}

/** Is `value` a real calendar day written `"YYYY-MM-DD"`? */
export function isDayKey(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === value;
}

/** Days since 1970-01-01 of a day key (its index on a plain day count). */
export function dayNumber(dayKey: string): number {
  return Math.round(Date.parse(`${dayKey}T00:00:00Z`) / DAY_MS);
}

/** The instant (UTC ms) the Tehran day `dayKey` begins. */
export function tehranDayStartMs(dayKey: string): number {
  return Date.parse(`${dayKey}T00:00:00Z`) - TEHRAN_OFFSET_MS;
}
